import { AxiosError } from 'axios';
import classnames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { DATE_FORMAT } from 'utils/constants';
import archiveFile from 'v2//icons/archive-file.svg';
import checkIconFilled from 'v2//icons/checkIconFilled.svg';
import { ReactComponent as ComentDots } from 'v2/icons/commentDots.svg';
import crossIconFilled from 'v2//icons/crossIconFilledRed.svg';
import listIcon from 'v2//icons/list.svg';
import emptyNotificationBottomImage from 'v2//images/emptyNotificationBottomImage.png';
import emptyNotificationTopImage from 'v2//images/emptyNotificationTopImage.png';
import { UseNotificationContext } from 'v2/context/notificationContext';
import {
  DjangoListResponse,
  GenericObject,
  NotificationEntity,
  NotificationType,
  ProposalEntity,
  SearchEntity,
  RootState,
} from 'v2/models';
import { handleError, ServerError } from 'v2/utils/handleError';
import { getProposalUrl } from 'v2/utils/proposalUtils';
import restFactory from 'v2/utils/restFactory';
import styles from './notificatons.module.scss';

interface NotificationView extends NotificationEntity {
  proposalEntity: ProposalEntity;
}

interface NotificationsProps {
  unreadNotifications: NotificationEntity[];
  refreshUnreadotifications: UseNotificationContext['refreshUnreadotifications'];
}

const NOTIFICATION_ICON: { [key in NotificationType]: string } = {
  ACCEPTED: checkIconFilled,
  ARCHIVED: archiveFile,
  REJECTED: crossIconFilled,
};

const DELAY_TRIGGER_READ = 1000 * 5;

const Notifications = ({
  unreadNotifications,
  refreshUnreadotifications,
}: NotificationsProps) => {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<NotificationView[]>([]);
  const [unreadIds, setUnReadIds] = useState<{ [key: number]: boolean }>({});
  const user = useSelector((state: RootState) => state.user.user);
  const proposals = useSelector((state: GenericObject) => state.proposals);

  useEffect(() => {
    async function loadNotifications() {
      try {
        setLoading(true);
        const response = await restFactory.get<
          DjangoListResponse<NotificationEntity>
        >('/dj/api/v1/notification/', { size: 100 });
        /*
        Proposals are not paginated and at this moment we are loading all 
        proposals in our Redux store, so we are finding the notification proposal
        using the already loaded proposals
      */
        setNotifications(
          response.results
            .map((notification) => ({
              ...notification,
              proposalEntity: proposals.multipleProposals.find(
                (proposal: ProposalEntity) =>
                  proposal.id === notification.proposal_id
              ),
            }))
            .filter((notification) => notification.proposalEntity)
        );
        setLoading(false);
      } catch (error) {
        handleError(error as AxiosError<ServerError>);
        setLoading(false);
      }
    }

    if (!notifications.length && proposals.loaded) {
      loadNotifications();
    }

    setUnReadIds(
      unreadNotifications.reduce(
        (acc, item) => ({ ...acc, [item.id]: true }),
        {}
      )
    );

    resetUnreadNotificaitons();

    return () => {
      resetUnreadNotificaitons();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadNotifications]);

  const resetUnreadNotificaitons = async () => {
    if (unreadNotifications.length) {
      setTimeout(async () => {
        try {
          await restFactory.post('/dj/api/v1/notification/viewed_all/', null);
          refreshUnreadotifications?.();
        } catch (error) {
          handleError(error as AxiosError<ServerError>);
        }
      }, DELAY_TRIGGER_READ);
    }
  };

  if (!loading && !notifications.length) {
    return (
      <div
        className={classnames(
          'fix-bg-images  h-100',
          'position-relative',
          'd-flex align-items-center justify-content-center',
          styles.emptyNotificationContainer
        )}
      >
        <img
          src={emptyNotificationTopImage}
          className={styles.emptyPageTopImage}
          alt="emptyTopImage"
        ></img>
        <h1 className="text-primary-dark text-center mb-5">
          You have no notifications yet!
        </h1>
        <img
          src={emptyNotificationBottomImage}
          className={styles.emptyPageBottomImage}
          alt="emptyBottomImage"
        ></img>
      </div>
    );
  }

  return (
    <Container className="fix-bg-images" fluid="xl">
      {notifications.map((item) => (
        <div
          key={item.id}
          className={classnames(
            styles.notificationItem,
            'border',
            'p-3',
            'mb-3',
            {
              'shadow-sm border-primary': unreadIds[item.id],
              'border-light5': !unreadIds[item.id],
            }
          )}
        >
          <div className="d-flex align-items-center text-truncate">
            <img
              src={NOTIFICATION_ICON[item.type]}
              className={classnames('mr-2', {
                [styles.archiveIcon]: item.type === 'ARCHIVED',
              })}
              alt="notificationIconStatus"
            />

            <p className="m-0 text-truncate">
              Your response for{' '}
              <span className={classnames(styles.searchName)}>
                {(item.proposalEntity?.search as SearchEntity)?.name}
              </span>{' '}
              was {item.type.toLocaleLowerCase()}
            </p>
          </div>

          <div className="text-light3 d-flex align-items-center">
            {moment(item.created_at).format(DATE_FORMAT.LONG)}
          </div>
          <div className="text-left">
            {item.type === 'ACCEPTED' ? (
              item.proposalEntity.data_provider_response?.submitted_by
                ?.username === user.username ? (
                <Link to="/discussions">
                  <ComentDots className="mr-2" />
                  View discussion
                </Link>
              ) : (
                <div className={styles.noOriginalResponder}>
                  <ComentDots
                    className={classnames('mr-2', styles.discussionIcon)}
                  />
                  <span>Colleague&apos;s Discussion</span>
                </div>
              )
            ) : null}
          </div>
          <Link
            to={`${getProposalUrl(item.proposalEntity)}#${
              item.proposalEntity.id
            }`}
            className="text-right"
          >
            <img src={listIcon} className="mr-2" alt="discussionIcon" />
            View proposal
          </Link>
        </div>
      ))}
    </Container>
  );
};

export default Notifications;
